import * as React from 'react';
import * as _ from 'underscore';

import { DataQualityType, localizeDataQualityTypeName } from '@tableau/data-catalog-client-components';
import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  CheckBoxWithLabelWidget,
  DialogPositioningBehavior,
  DialogType,
  DropdownButtonType,
  List,
  ListProps,
  SingleSelectDropdown,
  TabStyles
} from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { classes } from '../../style';
import { ReactModalOptions } from '../../ts/services/ReactModalService';
import { CharacterCounter } from '../content/CharacterCounter';
import {
  ButtonContainerDiv,
  checkboxStyle,
  ContentItemNameDiv,
  DATA_QUALITY_DIALOG_CANCEL_TEST_ID,
  DATA_QUALITY_DIALOG_SAVE_TEST_ID,
  DATA_QUALITY_DIALOG_TEST_ID,
  DATA_QUALITY_DIALOG_TOGGLE_TEST_ID,
  DATA_QUALITY_DIALOG_TYPE_TEST_ID,
  DropDownPromptDiv,
  dropDownStyle,
  HelpTextDiv,
  InputHintDiv,
  textAreaClass
} from '../content/styles/DataQualityStyles';
import {
  buttonStyleSubmitting,
  cancelButtonStyle,
  DIALOG_WIDTH,
  dialogBodyStyle,
  overLimitCounter
} from '../content/styles/ExternalContentActionsStyles';
import {
  dataQualityTypeListItem,
  dataQualityTypes,
  dataQualityTypeToString,
  stringToDataQualityType
} from './DataQualityType';

const MESSAGE_MAXIMUM_LENGTH: number = 4000;

const defaultInitialState: DataQualityDialogState = {
  isActive: false,
  type: dataQualityTypeToString(DataQualityType.WARNING),
  message: '',
};

export interface DataQualityDialogProps {
  readonly contentName: string;
  readonly initial?: DataQualityDialogState;
  readonly submit: (args: DataQualityDialogState) => void;
  readonly cancel: () => void;
}

export interface DataQualityDialogState {
  readonly isActive: boolean;
  readonly type: string;
  readonly message: string;
}

export interface DataQualityDialogStateAndLifecycle extends DataQualityDialogState {
  readonly isSubmitting: boolean;
  readonly isDirty: boolean;
  readonly isValid: boolean;
}

function getFormState(state: DataQualityDialogState | DataQualityDialogStateAndLifecycle): DataQualityDialogState {
  return {
    isActive: state.isActive,
    message: state.message,
    type: state.type
  };
}

function getInitialState(props: DataQualityDialogProps): DataQualityDialogState {
  return getFormState(props.initial || defaultInitialState);
}

function checkIsValid(current: DataQualityDialogState): boolean {
  return current.message.length <= MESSAGE_MAXIMUM_LENGTH;
}

function checkIsDirty(current: DataQualityDialogState, initial: DataQualityDialogState): boolean {
  return !_.isEqual(initial, current);
}

export class DataQualityDialog extends React.Component<DataQualityDialogProps, DataQualityDialogStateAndLifecycle> {
  constructor(props: DataQualityDialogProps) {
    super(props);
    const dialogState = props.initial || defaultInitialState;
    this.state = {
      ...dialogState,
      isSubmitting: false,
      isDirty: false,
      isValid: true,
    };
  }

  private handleCheckboxChange = (isActive: boolean) => {
    this.setState((state, props) => {
      const form = getFormState(state);
      const initial: DataQualityDialogState = getInitialState(props);
      const updated: DataQualityDialogState = { ...form, isActive };

      return { ...updated, isDirty: checkIsDirty(updated, initial) };
    });
  }

  private onUpdateType = (type?: string) => {
    this.setState((state, props) => {
      const form = getFormState(state);
      const initial: DataQualityDialogState = getInitialState(props);
      const updated: DataQualityDialogState = { ...form, type };

      return { ...updated, isDirty: checkIsDirty(updated, initial) };
    });
  }

  private onUpdateMessage = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const message = event.target.value;

    this.setState((state, props) => {
      const form = getFormState(state);
      const initial: DataQualityDialogState = getInitialState(props);
      const updated: DataQualityDialogState = { ...form, message };

      return { ...updated, isDirty: checkIsDirty(updated, initial), isValid: checkIsValid(updated) };
    });
  }

  private onSubmit = () => this.setState({ isSubmitting: true }, () => this.props.submit(getFormState(this.state)));

  private onCancel = () => this.setState({ isSubmitting: true }, this.props.cancel);

  public render(): React.ReactNode {
    return (
      <React.Fragment>
        <ContentItemNameDiv>{this.props.contentName}</ContentItemNameDiv>
        <CheckBoxWithLabelWidget
          label={Messages.dataQualityDialog_CheckboxLabel()}
          testId={DATA_QUALITY_DIALOG_TOGGLE_TEST_ID}
          checked={this.state.isActive}
          handleChange={this.handleCheckboxChange}
          removeLeftMargin={true}
          containerStyle={checkboxStyle} />
        <HelpTextDiv>{Messages.dataQualityDialog_Help()}</HelpTextDiv>
        <DropDownPromptDiv>{Messages.dataQualityDialog_DropdownPrompt()}</DropDownPromptDiv>
        <InputHintDiv>{Messages.dataQualityType()}</InputHintDiv>
        <SingleSelectDropdown
          testId={DATA_QUALITY_DIALOG_TYPE_TEST_ID}
          type={DropdownButtonType.Outline}
          list={this.DataQualityTypeList(this.state.type)}
          disabled={!this.state.isActive}
          value={localizeDataQualityTypeName(stringToDataQualityType(this.state.type))}
          onChange={this.onUpdateType}
          style={dropDownStyle} />
        <InputHintDiv>{Messages.dataQualityMessage()}</InputHintDiv>
        <textarea
          onChange={this.onUpdateMessage}
          disabled={!this.state.isActive}
          className={textAreaClass}
          value={this.state.message} />
        <CharacterCounter
          value={this.state.message}
          maximum={MESSAGE_MAXIMUM_LENGTH}
          style={this.state.isValid ? null : overLimitCounter} />
        <ButtonContainerDiv>
          <ButtonWidget
            disabled={this.state.isSubmitting}
            testId={DATA_QUALITY_DIALOG_CANCEL_TEST_ID}
            buttonType={ButtonType.Outline}
            handleClick={this.onCancel}
            style={cancelButtonStyle}>
            <span>{Messages.cancel()}</span>
          </ButtonWidget>
          <ButtonWidget
            disabled={this.state.isSubmitting || !this.state.isDirty || !this.state.isValid}
            testId={DATA_QUALITY_DIALOG_SAVE_TEST_ID}
            buttonType={ButtonType.Go}
            style={ this.state.isSubmitting ? buttonStyleSubmitting : undefined }
            handleClick={this.onSubmit}>
            {
              this.state.isSubmitting ?
                <div style={{ marginTop: TabStyles.Spacing.SmallSpace! }}>
                  <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Small} />
                </div> :
                <span>{Messages.save()}</span>
            }
          </ButtonWidget>
        </ButtonContainerDiv>
      </React.Fragment>
    );
  }

  private DataQualityTypeList(selected: string): React.ReactElement<ListProps> {
    return <List items={dataQualityTypes.map(dataQualityTypeListItem)} selectedValue={selected.toString()} />;
  }
}

export function dataQualityDialogProps(props: DataQualityDialogProps): ReactModalOptions {
  return {
    title: Messages.dataQualityDialog_Title(),
    testId: DATA_QUALITY_DIALOG_TEST_ID,
    type: DialogType.NormalWithTitleBar,
    showCloseButton: false,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogWidth: DIALOG_WIDTH,
    dialogBodyStyle: dialogBodyStyle,
    dialogContent: <DataQualityDialog {...props} />
  };
}
